/**
 * What stands between a mealtime and Confirm, in words (PROD-1511).
 *
 * The flow's sign gate is a single boolean, so a disabled Confirm used to say
 * nothing about why (Treasure Coast, Oct 9: one Served above its Received kept
 * Breakfast unsignable and nothing on screen said so). The phone review lists
 * these at the top and its main button reads "Fix N things" until the list is
 * empty. The flow builds the input from the SAME facts its gate reads, and
 * adds a catch-all line if the gate is shut with nothing listed, so the count
 * can never claim zero while Confirm is off.
 *
 * Pure — no React.
 */

import type { RecordColumnKey } from '@/components/production/spreadsheet/record-spreadsheet-table'

/** Where a blocker's fix lives: a cell to open, a card to scroll to, or nowhere specific. */
export type StepBlockerTarget =
  | { kind: 'cell'; rowKey: string; column: RecordColumnKey }
  | { kind: 'card'; card: 'delivery' | 'service' | 'writeIns' }
  | null

export interface StepBlocker {
  key: string
  /** One line saying what to fix. */
  text: string
  target: StepBlockerTarget
}

export interface StepBlockerRow {
  key: string
  /** The row's name with its band: `Fruit Cup K-12`. */
  name: string
  /** Reimbursable + non-reimbursable no longer add up to Served (the server would refuse the save). */
  blockingViolation: boolean
  /** The row stands on a truck and its scope's drop-off reading is empty. Set on ONE row per scope. */
  missingReceivedTemp: boolean
  /** The row is on the record and its scope's serving reading is empty. Set on ONE row per scope. */
  missingServingTemp: boolean
}

export interface StepBlockerInput {
  rows: readonly StepBlockerRow[]
  /** Something is to be received and nobody is named. */
  receiverMissing: boolean
  /** A record step with no signature typed. */
  signatureMissing: boolean
  /** An amendment with no reason. */
  amendReasonMissing: boolean
  /** A write-in row was started and not finished. */
  incompleteWriteIn: boolean
  /** A delivered line still lacks a reading that no row shows as missing (a line-level gap). */
  lineTempMissing: boolean
  /** A step with no record and nothing to receive. */
  nothingToConfirm: boolean
}

/** Past this many, missing readings of one kind collapse into one line. */
const MAX_LISTED_READINGS = 3

/** "1 thing to fix before you confirm", "3 things to fix before you confirm". */
export function blockersTitle(count: number): string {
  return count === 1 ? '1 thing to fix before you confirm' : `${count} things to fix before you confirm`
}

/** The review's main button while something blocks: "Fix 1 thing", "Fix 3 things". */
export function fixLabel(count: number): string {
  return count === 1 ? 'Fix 1 thing' : `Fix ${count} things`
}

function readingLines(
  rows: readonly StepBlockerRow[],
  pick: (row: StepBlockerRow) => boolean,
  column: 'receivedTemp' | 'servingTemp',
  noun: string,
): StepBlocker[] {
  const missing = rows.filter(pick)
  if (missing.length === 0) {
    return []
  }
  if (missing.length > MAX_LISTED_READINGS) {
    const first = missing[0]!
    return [
      {
        key: `${column}:all`,
        text: `${missing.length} items need a ${noun}.`,
        target: { kind: 'cell', rowKey: first.key, column },
      },
    ]
  }
  return missing.map((row) => ({
    key: `${column}:${row.key}`,
    text: `${row.name}: add the ${noun}.`,
    target: { kind: 'cell', rowKey: row.key, column },
  }))
}

/**
 * The list, in the order a person would fix things: the rows top to bottom
 * (counts first, then readings), then the cards under them (write-ins,
 * delivery, service).
 */
export function buildStepBlockers(input: StepBlockerInput): StepBlocker[] {
  const blockers: StepBlocker[] = []
  for (const row of input.rows) {
    if (row.blockingViolation) {
      blockers.push({
        key: `split:${row.key}`,
        text: `${row.name}: Reimb. and Non-reimb. must add up to Served.`,
        target: { kind: 'cell', rowKey: row.key, column: 'reimbursable' },
      })
    }
  }
  blockers.push(...readingLines(input.rows, (row) => row.missingReceivedTemp, 'receivedTemp', 'drop-off temperature'))
  blockers.push(...readingLines(input.rows, (row) => row.missingServingTemp, 'servingTemp', 'serving temperature'))
  if (input.lineTempMissing) {
    blockers.push({ key: 'line-temp', text: 'A delivered item still needs its drop-off temperature.', target: null })
  }
  if (input.incompleteWriteIn) {
    blockers.push({
      key: 'write-in',
      text: 'Finish the write-in you started, or remove it.',
      target: { kind: 'card', card: 'writeIns' },
    })
  }
  if (input.receiverMissing) {
    blockers.push({
      key: 'receiver',
      text: 'Add who received the delivery.',
      target: { kind: 'card', card: 'delivery' },
    })
  }
  if (input.amendReasonMissing) {
    blockers.push({
      key: 'amend-reason',
      text: 'Say why you are changing the record.',
      target: { kind: 'card', card: 'service' },
    })
  }
  if (input.signatureMissing) {
    blockers.push({
      key: 'signature',
      text: 'Sign the service with your full name.',
      target: { kind: 'card', card: 'service' },
    })
  }
  if (input.nothingToConfirm) {
    blockers.push({ key: 'nothing', text: 'There is nothing to confirm on this mealtime yet.', target: null })
  }
  return blockers
}

/** The catch-all for a shut gate with nothing listed, so "Fix N things" never reads zero while Confirm is off. */
export const OTHER_BLOCKER: StepBlocker = {
  key: 'other',
  text: 'Something on this mealtime still needs a value.',
  target: null,
}
